/* eslint-disable @typescript-eslint/no-explicit-any */
// React 18 rendering adapter for @a2ui/web_core v0.9 surfaces. Reimplements
// the recipe of the official @a2ui/react renderer (React 19 peer) without
// that package: recursive nodes, useSyncExternalStore over the component
// model events for arrival detection, a GenericBinder per component bridged
// to React with useSyncExternalStore, and disposal on unmount.
//
// Hooks discipline mirrors the official renderer: the deferred node runs the
// arrival check and may return early; the resolved node receives a verified
// component model and runs every binder hook unconditionally.
//
// Unknown component types render nothing — the closed catalog is the
// security boundary between model output and the DOM.

import type { ReactNode } from "react";
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from "react";

import type { ComponentModel, SurfaceModel } from "@a2ui/web_core/v0_9";
import { ComponentContext, GenericBinder } from "@a2ui/web_core/v0_9";

import { colorForColorIndex, REPORT_ROOT_ID } from "./contract";
import {
  ReportChartView,
  ReportMetricCardsView,
  ReportTableGridView,
  ReportTextView
} from "./ReportComponents";
import { useSeenWhenVisible } from "./useSeenWhenVisible";

/** Which blocks a rewrite changed, and which of those the reader has seen. */
export interface ChangeMarks {
  changedIds: readonly string[];
  seenIds: readonly string[];
  onSeen: (id: string) => void;
}

const NO_MARKS: ChangeMarks = { changedIds: [], seenIds: [], onSeen: () => {} };

// A context because DeferredNode/ResolvedNode are recursive and memoized, so a
// prop would have to be threaded through every level.
const PrintModeContext = createContext(false);
const ArrivalAnimationContext = createContext(false);
const ChangeMarksContext = createContext(NO_MARKS);

interface ResolvedChild {
  id: string;
  basePath: string;
}

type ReportChild = string | ResolvedChild;

interface SectionProps {
  children?: ReportChild[];
  buildChild: (id: string, basePath?: string) => ReactNode;
}

const MARK_FADE =
  "transition-opacity duration-700 motion-reduce:transition-none";

// One slot of a section. A block that a rewrite changed carries a line and a
// label until the reader has looked at it; both fade rather than vanish, and
// stay mounted so the block itself is never rebuilt.
function BlockSlot({ id, children }: { id: string; children: ReactNode }) {
  const isPrint = useContext(PrintModeContext);
  const { changedIds, seenIds, onSeen } = useContext(ChangeMarksContext);

  const isChanged = !isPrint && changedIds.includes(id);
  const isUnseen = isChanged && !seenIds.includes(id);
  const markSeen = useCallback(() => onSeen(id), [onSeen, id]);
  const ref = useSeenWhenVisible(isUnseen, markSeen);
  const fadeOut = isUnseen ? "" : "opacity-0";
  const changeState = isUnseen ? "unseen" : "seen";

  return (
    <div
      ref={ref}
      className={isChanged ? "relative" : undefined}
      data-change={isChanged ? changeState : undefined}
    >
      {isChanged && (
        <>
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-y-0 -left-3 w-0.5 rounded-full bg-teal-500 ${MARK_FADE} ${fadeOut}`}
          />
          <span
            aria-hidden={!isUnseen}
            className={`pointer-events-none absolute -top-[18px] right-0 rounded-full bg-teal-50 px-2 py-px text-[11px] font-semibold leading-4 text-teal-700 ${MARK_FADE} ${fadeOut}`}
          >
            Updated
          </span>
        </>
      )}
      {children}
    </div>
  );
}

// ReportSection is the only layout primitive in the catalog: it renders its
// resolved children in order through the recursive builder.
function SectionRenderer({ children = [], buildChild }: SectionProps) {
  return (
    <div className="flex flex-col gap-6">
      {(children ?? []).map((child, index) =>
        typeof child === "string" ? (
          <BlockSlot key={`${child}-${index}`} id={child}>
            {buildChild(child)}
          </BlockSlot>
        ) : (
          <BlockSlot key={`${child.id}-${index}`} id={child.id}>
            {buildChild(child.id, child.basePath)}
          </BlockSlot>
        )
      )}
    </div>
  );
}

// Subscribes to component-model events for one id so a node re-renders when
// its model arrives, updates, or is removed mid-stream.
function useArrivalVersion(surface: SurfaceModel<any>, id: string): number {
  const store = useMemo(() => {
    let version = 0;
    return {
      subscribe: (callback: () => void) => {
        const created = surface.componentsModel.onCreated.subscribe((comp) => {
          if (comp.id === id) {
            version += 1;
            callback();
          }
        });
        const deleted = surface.componentsModel.onDeleted.subscribe(
          (deletedId) => {
            if (deletedId === id) {
              version += 1;
              callback();
            }
          }
        );
        return () => {
          created.unsubscribe();
          deleted.unsubscribe();
        };
      },
      getSnapshot: () => version
    };
  }, [surface, id]);

  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}

interface ResolvedNodeProps {
  surface: SurfaceModel<any>;
  id: string;
  basePath: string;
  componentModel: ComponentModel;
  componentApi: any;
}

function ResolvedNode({
  surface,
  id,
  basePath,
  componentModel,
  componentApi
}: ResolvedNodeProps) {
  const isPrint = useContext(PrintModeContext);
  const context = useMemo(
    () => new ComponentContext(surface, id, basePath),
    // componentModel is read inside ComponentContext; it is the trigger for
    // recreation even though it is not a dependency value below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surface, id, basePath, componentModel]
  );

  const bindingRef = useRef<{
    context: ComponentContext;
    binder: GenericBinder<any>;
  } | null>(null);
  if (!bindingRef.current) {
    bindingRef.current = {
      context,
      binder: new GenericBinder(context, componentApi.schema)
    };
  } else if (bindingRef.current.context !== context) {
    bindingRef.current.binder.dispose();
    bindingRef.current = {
      context,
      binder: new GenericBinder(context, componentApi.schema)
    };
  }
  const binder = bindingRef.current.binder;

  const subscribe = useCallback(
    (callback: () => void) => {
      // The first subscribe() connects the binder to the component model and
      // the data context, so dynamic paths stay live; the last unsubscribe()
      // disposes it.
      const subscription = binder.subscribe(callback);
      return () => subscription.unsubscribe();
    },
    [binder]
  );
  const getSnapshot = useCallback(() => binder.snapshot, [binder]);
  const props = useSyncExternalStore(subscribe, getSnapshot);

  // Unmount-only teardown. The dispose function is kept in a ref because the
  // binder instance can change on context recreation; the memo runs once and
  // returns the cleanup that runs when the node unmounts.
  const disposeRef = useRef<() => void>(() => {});
  disposeRef.current = () => binder.dispose();

  useMemo(() => () => disposeRef.current(), []);

  const buildChild = useCallback(
    (childId: string, specificPath?: string) => (
      <DeferredNode
        surface={surface}
        id={childId}
        basePath={specificPath ?? context.dataContext.path}
      />
    ),
    [surface, context.dataContext.path]
  );

  switch (componentModel.type) {
    case "ReportSection":
      return (
        <SectionRenderer children={props.children} buildChild={buildChild} />
      );
    case "ReportText":
      return <ReportTextView markdown={props.markdown} />;
    case "ReportChart":
      return (
        <ReportChartView
          title={props.title}
          baseChart={props.baseChart}
          orientation={props.orientation}
          xAxisKey={props.xAxisKey}
          series={(props.series ?? []).map((item: any) => ({
            dataKey: item.dataKey,
            name: item.name,
            color: colorForColorIndex(item.colorIndex)
          }))}
          data={props.data}
          isPrint={isPrint}
        />
      );
    case "ReportKpi":
      return <ReportMetricCardsView cards={props.cards} />;
    case "ReportTable":
      return <ReportTableGridView columns={props.columns} rows={props.rows} />;
    default:
      return null;
  }
}

const ResolvedNodeMemo = memo(ResolvedNode);

interface DeferredNodeProps {
  surface: SurfaceModel<any>;
  id: string;
  basePath: string;
}

function DeferredNode({ surface, id, basePath }: DeferredNodeProps) {
  useArrivalVersion(surface, id);

  // Read once, when the slot first mounts: a block already on screen must not
  // replay its entrance when a rewrite flips the flag back on.
  const animateArrivals = useContext(ArrivalAnimationContext);
  const [animatesIn] = useState(animateArrivals);

  const componentModel = surface.componentsModel.get(id);
  if (!componentModel) return null;

  const componentApi = surface.catalog.components.get(componentModel.type);
  if (!componentApi) return null;

  const node = (
    <ResolvedNodeMemo
      surface={surface}
      id={id}
      basePath={basePath}
      componentModel={componentModel}
      componentApi={componentApi}
    />
  );

  // Sections only hold blocks, and animating both would stack the two.
  if (!animatesIn || componentModel.type === "ReportSection") return node;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-3 duration-500 motion-reduce:animate-none">
      {node}
    </div>
  );
}

const DeferredNodeMemo = memo(DeferredNode);

interface ReportSurfaceProps {
  surface: SurfaceModel<any>;
  /** Lays charts out for paper: no pagination, scrolling or animation. */
  isPrint?: boolean;
  /** Eases in each block that mounts while this is true, i.e. while written. */
  animateArrivals?: boolean;
  /** Labels the blocks a rewrite changed until the reader has seen them. */
  changeMarks?: ChangeMarks;
}

export function ReportSurface({
  surface,
  isPrint = false,
  animateArrivals = false,
  changeMarks = NO_MARKS
}: ReportSurfaceProps) {
  return (
    <PrintModeContext.Provider value={isPrint}>
      <ArrivalAnimationContext.Provider value={animateArrivals && !isPrint}>
        <ChangeMarksContext.Provider value={changeMarks}>
          <div data-surface-id={surface.id} className="flex flex-col gap-4">
            <DeferredNodeMemo
              surface={surface}
              id={REPORT_ROOT_ID}
              basePath="/"
            />
          </div>
        </ChangeMarksContext.Provider>
      </ArrivalAnimationContext.Provider>
    </PrintModeContext.Provider>
  );
}
